import { CantileverButton } from 'components/CantileverButton'
import {
  KNOWLEDGE_DOC_PAGE_NAVIGATE,
  useGeckoAnalytics,
} from 'features/analytics'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import {
  TooltipOption,
  useKnowledgeDocumentUXContext,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import styled from 'styled-components'

const PageSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const PageText = styled.span`
  font-size: 0.875rem;
  color: ${(props) => props.theme.forecast.defaultText};
  white-space: nowrap;
  user-select: none;
  font-variant-numeric: tabular-nums;
`

export function PageSelector() {
  const {
    activeTabId,
    updateDocumentHistoryItem,
    tooltipOptions,
    isOnDocument,
    currentPageNumber,
    currentDocumentId,
  } = useKnowledgeDocumentUXContext()
  const { totalPages } = useKnowledgeDocumentDataContext()
  const { trackEvent } = useGeckoAnalytics()

  const multiPageTurner = tooltipOptions.includes(TooltipOption.MultiPageTurner)
  const showMultiplePages = tooltipOptions.includes(
    TooltipOption.ShowMultiplePages
  )
  const pageStep = multiPageTurner ? 2 : 1

  const handlePreviousPage = () => {
    if (currentPageNumber > 1) {
      const newPageNumber = Math.max(1, currentPageNumber - pageStep)
      trackEvent(KNOWLEDGE_DOC_PAGE_NAVIGATE.title, {
        direction: 'previous',
        from_page: currentPageNumber,
        to_page: newPageNumber,
        total_pages: totalPages,
        document_id: currentDocumentId,
      })
      updateDocumentHistoryItem(activeTabId, {
        pageNumber: newPageNumber,
        selectedBoxIds: [],
      })
    }
  }

  const handleNextPage = () => {
    if (currentPageNumber < totalPages) {
      const newPageNumber = Math.min(totalPages, currentPageNumber + pageStep)
      trackEvent(KNOWLEDGE_DOC_PAGE_NAVIGATE.title, {
        direction: 'next',
        from_page: currentPageNumber,
        to_page: newPageNumber,
        total_pages: totalPages,
        document_id: currentDocumentId,
      })
      updateDocumentHistoryItem(activeTabId, {
        pageNumber: newPageNumber,
        selectedBoxIds: [],
      })
    }
  }

  if (!isOnDocument || totalPages <= 1) {
    return null
  }

  const hasSecondPage =
    showMultiplePages && totalPages > 1 && currentPageNumber < totalPages
  const pageText = hasSecondPage
    ? `Pages ${currentPageNumber}-${currentPageNumber + 1} / ${totalPages}`
    : `Page ${currentPageNumber} / ${totalPages}`

  return (
    <PageSelectorContainer>
      <CantileverButton
        ghost
        iconOnly
        title='Previous Page'
        onClick={handlePreviousPage}
        disabled={currentPageNumber <= 1}>
        <ChevronLeft width={20} height={20} />
      </CantileverButton>
      <PageText>{pageText}</PageText>
      <CantileverButton
        ghost
        iconOnly
        title='Next Page'
        onClick={handleNextPage}
        disabled={currentPageNumber >= totalPages}>
        <ChevronRight width={20} height={20} />
      </CantileverButton>
    </PageSelectorContainer>
  )
}
